/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { JOB_INVOICED_TYPES } from "@constants";
import { ReactSelect, Tooltip } from "../../../../components";

const PaymentDetails = ({
	form,
	user,
	clientContacts,
	updateForm,
	openQuickAddContactModal,
	validateSection,
	isNewJob,
	expandNextSection,
	positionWithPayCodeExist,
	updateTransferPaymentsToAgency,
	transferPaymentToJurisdictionEnabled,
	transferPaymentsToAgencyRuleEnabled,
	clients,
	clientId,
	loadingClients,
	requireInvoicingForVehicles,
	jobs,
	openForcedVehicleInvoicingModal,
}) => {
	const { lang } = global;
	const prepayEnabledForOrg = user.Features.some(f => f.toLowerCase().includes("prepay"));
	const isInvoicingV3 = user.Features.some(f => f.toLowerCase() === "invoicingv3");
	const isInvoicingV2 = user.Organization.NonRKPayInvoicingEnabled && !isInvoicingV3;
	const clientBillingContactRef = useRef();
	const [payCodeWasAdded, setPayCodeWasAdded] = useState(positionWithPayCodeExist);
	const [forceVehicleInvoicing, setForceVehicleInvoicing] = useState(false);
	const [isTransferPaymentsToAgencyDefault, setIsTransferPaymentsToAgencyDefault] =
		useState(false);

	const nonRKPayTransferOfficerPaymentsToAgencyEnabled =
		transferPaymentsToAgencyRuleEnabled &&
		!transferPaymentToJurisdictionEnabled &&
		isInvoicingV3 &&
		(user.IsInternalCoordinator || user.isLEA());
	const RKPayTransferOfficerPaymentsToAgencyEnabled =
		transferPaymentsToAgencyRuleEnabled &&
		!transferPaymentToJurisdictionEnabled &&
		(user.IsInternalCoordinator || user.isLEA());
	const validateForm = newForm => {
		let isValid = true;
		const formToValidate = newForm ? newForm.paymentDetails : form;

		if (
			formToValidate.JobInvoicedTypeId === null ||
			(formToValidate.JobInvoicedTypeId !== JOB_INVOICED_TYPES.OFFICER_INVOICING &&
				(!user.Organization.NonRKPayInvoicingEnabled ||
					formToValidate.JobInvoicedTypeId === JOB_INVOICED_TYPES.SCHEDULE_ONLY ||
					(formToValidate.JobInvoicedTypeId === JOB_INVOICED_TYPES.AGENCY_INVOICING &&
						isInvoicingV2)) &&
				(formToValidate.PaymentMethod?.trim() === "" || !formToValidate.PaymentMethod))
		) {
			isValid = false;
		} else if (
			!user.isAgencyAdmin() &&
			formToValidate.JobInvoicedTypeId === JOB_INVOICED_TYPES.OFFICER_INVOICING &&
			formToValidate.ClientBillingContactId === ""
		) {
			isValid = false;
		}

		validateSection("paymentDetails", isValid);
	};

	// If we are editing/duplicating a job/draft, validation should update immediately.
	// When creating a new job, we want the user to go through all the steps instead
	useEffect(() => {
		if (
			!isNewJob ||
			(clientBillingContactRef.current &&
				clientBillingContactRef.current !== form.ClientBillingContactId)
		) {
			validateForm();
			// update clientBillingContactRef to match form value
			clientBillingContactRef.current = form.ClientBillingContactId;
		} else if (!clientBillingContactRef.current && form.ClientBillingContactId !== "") {
			clientBillingContactRef.current = form.ClientBillingContactId;
		}
	}, [form]);
	// Use effect functionality to auto populate the payment Method field on the first render.
	useEffect(() => {
		if (form.PaymentMethod === "" && isNewJob) {
			updateForm("paymentDetails", { PaymentMethod: user.Organization.PaymentDetails });
		}
	}, []);

	useEffect(() => {
		setPayCodeWasAdded(positionWithPayCodeExist);
		if (positionWithPayCodeExist) {
			updateForm(
				"paymentDetails",
				{
					RequirePrepayment: false,
				},
				false,
			);
		}
	}, [positionWithPayCodeExist]);

	useEffect(() => {
		if (!loadingClients && clientId && clientId !== "") {
			const selectedClient = clients.find(c => c.value === clientId);
			setIsTransferPaymentsToAgencyDefault(selectedClient.TransferOfficerPaymentsToAgency);
		}
	}, [clientId, loadingClients]);

	const isVehicleInvoicingForced = jobsList => {
		if (requireInvoicingForVehicles && jobsList.length > 0) {
			return jobsList.some(j => {
				const { VehicleRequired, VehicleTypes } = j;
				return VehicleRequired && VehicleTypes.length > 0;
			});
		}
		return false;
	};

	const handleForceVehicleInvoicingChange = value => {
		setForceVehicleInvoicing(value);
		if (value) {
			updateForm(
				"paymentDetails",
				{
					JobInvoicedTypeId:
						form.JobInvoicedTypeId === JOB_INVOICED_TYPES.SCHEDULE_ONLY
							? null
							: form.JobInvoicedTypeId,
					IsScheduling: false,
				},
				true,
				validateForm,
			);
		}
	};

	useEffect(() => {
		const isVehicleInvoicingRequired = isVehicleInvoicingForced(jobs);
		if (
			(forceVehicleInvoicing && !isVehicleInvoicingRequired) ||
			(!forceVehicleInvoicing && isVehicleInvoicingRequired)
		) {
			handleForceVehicleInvoicingChange(isVehicleInvoicingRequired);
		}
	}, [jobs]);

	const handleIsRKPayChange = value => () => {
		if (value === false) {
			updateForm(
				"paymentDetails",
				{
					IsRKPay: value,
					IsScheduling: false,
					JobInvoicedTypeId: JOB_INVOICED_TYPES.AGENCY_INVOICING,
					PaymentMethod: user.Organization.PaymentDetails,
				},
				true,
				() => true,
			);
		} else {
			updateForm(
				"paymentDetails",
				{
					IsRKPay: value,
					IsScheduling: false,
					JobInvoicedTypeId: JOB_INVOICED_TYPES.OFFICER_INVOICING,
				},
				true,
				() => true,
			);
		}
	};

	const handleIsSchedulingChange = value => () => {
		if (forceVehicleInvoicing) {
			openForcedVehicleInvoicingModal();
			return;
		}
		updateForm(
			"paymentDetails",
			{
				IsRKPay: false,
				JobInvoicedTypeId: JOB_INVOICED_TYPES.SCHEDULE_ONLY,
				IsScheduling: value,
			},
			true,
			() => true,
		);
	};

	const handleOrgFeeBlur = ({ target: { value } }) => {
		const intValue = parseInt(value, 10);
		if (value.indexOf("-") !== -1 || intValue < 0 || Number.isNaN(intValue)) {
			updateForm("paymentDetails", { OrganizationFee: "0" }, true);
		} else {
			validateForm();
		}
	};

	const handleBillingContactChange = event => {
		updateForm(
			"paymentDetails",
			{
				ClientBillingContactId: event?.value || "",
			},
			false,
			validateForm,
		);
	};

	const handleContinue = () => {
		if (form.OrganizationFee === "") {
			updateForm("paymentDetails", { OrganizationFee: "0" }, false);
		} else {
			updateForm("paymentDetails", {
				OrganizationFee: form.OrganizationFee || "0",
				ClientBillingContactId: form.ClientBillingContactId || "",
			});
		}
		validateForm();
		expandNextSection("paymentDetails");
	};

	return (
		<div id="payment-details-container">
			<If condition={prepayEnabledForOrg && (user.isLEA() || user.isCoordinator())}>
				<div className="row">
					<div className="col-xs-12 form-group payment-details-formgroup">
						<label
							htmlFor="requirePrepay"
							className={form.JobInvoicedTypeId !== 1 ? "invisible" : ""}
							title={
								payCodeWasAdded
									? lang.t(
											"To enable Officer Invoicing, you must remove paycodes from all positions",
										)
									: ""
							}
						>
							<input
								type="checkbox"
								id="requirePrepay"
								className="payment-details-checkbox"
								checked={form.RequirePrepayment}
								onChange={() =>
									updateForm(
										"paymentDetails",
										{
											RequirePrepayment: !form.RequirePrepayment,
										},
										false,
									)
								}
								disabled={payCodeWasAdded}
								data-test-id="jobs__require-prepay"
							/>
							{lang.t("Require Prepay")}
						</label>
					</div>
				</div>
			</If>
			<div className="row">
				<div className="col-xs-12">
					{lang.t("What type of invoicing do you want to use_")}
				</div>
			</div>
			<div className="row form-group payment-details-formgroup">
				<div className="col-xs-4 radio">
					<label
						htmlFor="isRKPay_true"
						title={
							payCodeWasAdded
								? lang.t(
										"To enable Officer Invoicing, you must remove paycodes from all positions",
									)
								: ""
						}
					>
						<input
							type="radio"
							name="isRKPay"
							id="isRKPay_true"
							onChange={handleIsRKPayChange(true)}
							checked={
								form.JobInvoicedTypeId === JOB_INVOICED_TYPES.OFFICER_INVOICING
							}
							data-test-id="jobs__is-rk-pay-true"
						/>
						{lang.t("Officer Invoicing")}
					</label>
				</div>
				<If
					condition={
						user.Organization.NonRKPayInvoicingEnabled ||
						user.Organization.NonRKPayPaymentsEnabled ||
						isInvoicingV3
					}
				>
					<div className="col-xs-4 radio">
						<label
							htmlFor="isRKPay_false"
							className={form.RequirePrepayment ? "item-disabled" : ""}
						>
							<input
								type="radio"
								name="isRKPay"
								id="isRKPay_false"
								onChange={handleIsRKPayChange(false)}
								checked={
									form.JobInvoicedTypeId === JOB_INVOICED_TYPES.AGENCY_INVOICING
								}
								disabled={form.RequirePrepayment}
							/>
							{lang.t("Agency Invoicing")}
						</label>
					</div>
				</If>
				<div className="col-xs-4 radio">
					<label
						htmlFor="isSchedulingOnly"
						className={form.RequirePrepayment ? "item-disabled" : ""}
					>
						<input
							type="radio"
							name="isSchedulingOnly"
							id="isSchedulingOnly"
							onChange={handleIsSchedulingChange(true)}
							checked={form.JobInvoicedTypeId === JOB_INVOICED_TYPES.SCHEDULE_ONLY}
							disabled={form.RequirePrepayment}
						/>
						{lang.t("Scheduling Only")}
					</label>
				</div>
			</div>
			<If
				condition={
					form.JobInvoicedTypeId !== JOB_INVOICED_TYPES.SCHEDULE_ONLY &&
					(user.isCoordinator() || user.isLEA())
				}
			>
				<If
					condition={
						form.JobInvoicedTypeId === JOB_INVOICED_TYPES.OFFICER_INVOICING ||
						(form.JobInvoicedTypeId === JOB_INVOICED_TYPES.AGENCY_INVOICING &&
							isInvoicingV3)
					}
				>
					<div className="row">
						<div className="col-xs-12">
							<label>{lang.t("Set Coordination Fee")}</label>
						</div>
						<div className="col-xs-12 col-sm-6 form-group">
							<div className="coordination-fee">
								<input
									type="number"
									className="form-control"
									value={form.OrganizationFee}
									name="jobOrganizationFee"
									id="jobOrganizationFee"
									onChange={({ target: { value } }) =>
										updateForm(
											"paymentDetails",
											{
												OrganizationFee: value,
											},
											true,
										)
									}
									onBlur={event => handleOrgFeeBlur(event)}
									placeholder="$"
									min="0"
									data-test-id="jobs__job-organization-fee"
								/>
								<p>/ Hour</p>
							</div>
						</div>
					</div>
				</If>
				<If condition={form.JobInvoicedTypeId === JOB_INVOICED_TYPES.OFFICER_INVOICING}>
					<div className="row">
						<div className="col-xs-6 col-sm-4">
							<label>
								{lang.t("Billing Contact")}
								<span className="required-mark" />
							</label>
						</div>
						<div className="col-xs-6 col-sm-2 text-right">
							<button
								type="button"
								className="btn btn-link open-add-contact-modal btn-blue-small"
								onClick={() => openQuickAddContactModal("contacts")}
								data-test-id="jobs__open-add-contact-modal"
							>
								{`+ ${lang.t("New")}`}
							</button>
						</div>
					</div>

					<div className="row">
						<div
							className={classNames("col-xs-12 col-sm-6 form-group", {
								"invalid-field":
									!user.isAgencyAdmin() &&
									form.IsRKPay &&
									form.ClientBillingContactId === "",
							})}
						>
							<ReactSelect
								id="client-billing-contact-id"
								name="ClientBillingContactId"
								value={form.ClientBillingContactId}
								onChange={handleBillingContactChange}
								options={clientContacts}
								isClearable
								data-test-id="jobs__client-billing-contact-id"
							/>
						</div>
					</div>
				</If>
			</If>
			<If
				condition={
					form.JobInvoicedTypeId === 3 || (form.JobInvoicedTypeId === 2 && isInvoicingV2)
				}
			>
				<div
					className={classNames("row form-group textarea-container", {
						"invalid-field":
							!form.IsRKPay &&
							(form.PaymentMethod?.trim() === "" || !form.PaymentMethod),
					})}
				>
					<label>
						{lang.t("Payment Details")}
						<If condition={!form.IsRKPay}>
							<span className="required-mark" />
						</If>
					</label>
					<textarea
						value={form.PaymentMethod}
						name="paymentMethod"
						id="paymentMethod"
						className="form-control"
						onChange={({ target: { value } }) =>
							updateForm("paymentDetails", { PaymentMethod: value }, false)
						}
						onBlur={() => validateForm()}
						placeholder={lang.t("How will payments take place_")}
						rows={4}
					/>
				</div>
			</If>
			<If condition={user.isLEA() && form.JobInvoicedTypeId !== 3}>
				<div className="row">
					<div className="col-xs-12">
						<input
							type="checkbox"
							name="waiveJurisdictionFees"
							id="waiveJurisdictionFees"
							className="payment-details-checkbox"
							checked={form.WaiveJurisdictionFees}
							onChange={() =>
								updateForm(
									"paymentDetails",
									{
										WaiveJurisdictionFees: !form.WaiveJurisdictionFees,
									},
									true,
								)
							}
							onBlur={() => validateForm()}
							data-test-id="jobs__waive-jurisdiction-fees"
						/>
						{lang.t("Waive Jurisdiction Fees")}
					</div>
				</div>
			</If>
			<If
				condition={
					((form.IsRKPay && RKPayTransferOfficerPaymentsToAgencyEnabled) ||
						(!form.IsRKPay && nonRKPayTransferOfficerPaymentsToAgencyEnabled)) &&
					form.JobInvoicedTypeId !== JOB_INVOICED_TYPES.SCHEDULE_ONLY
				}
			>
				<div className="row">
					<div className="col-xs-12 checkbox-container">
						<input
							type="checkbox"
							name="job-transfer-payments"
							id="job-transfer-payments"
							className="payment-details-checkbox"
							defaultChecked={isTransferPaymentsToAgencyDefault}
							checked={form.TransferPaymentsToAgency}
							onChange={({ target: { checked } }) =>
								updateTransferPaymentsToAgency(checked)
							}
							onBlur={() => validateForm()}
						/>
						<span>{lang.t("Transfer officer payments to agency")}</span>
						<Tooltip
							content={lang.t(
								"The full amount of the invoice will be paid to the agency instead of being distributed to the police officers_",
							)}
						>
							<i className="hidden-xs rkf rkf-question-solid tooltip-icon" />
						</Tooltip>
					</div>
				</div>
			</If>
			<div className="text-center continue-button-container">
				<button
					type="button"
					id="payment-details-continue"
					onClick={() => handleContinue()}
					className="btn btn-primary btn-big"
					data-test-id="jobs__payment-details-continue"
				>
					{lang.t("Continue")}
				</button>
			</div>
		</div>
	);
};

PaymentDetails.propTypes = {
	form: PropTypes.object.isRequired,
	user: PropTypes.object.isRequired,
	updateForm: PropTypes.func.isRequired,
	validateSection: PropTypes.func.isRequired,
	expandNextSection: PropTypes.func.isRequired,
	openQuickAddContactModal: PropTypes.func.isRequired,
	isNewJob: PropTypes.bool,
	clientContacts: PropTypes.array,
	positionWithPayCodeExist: PropTypes.bool,
	updateTransferPaymentsToAgency: PropTypes.func.isRequired,
	transferPaymentToJurisdictionEnabled: PropTypes.bool.isRequired,
	transferPaymentsToAgencyRuleEnabled: PropTypes.bool.isRequired,
	clients: PropTypes.array,
	clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	loadingClients: PropTypes.bool.isRequired,
	requireInvoicingForVehicles: PropTypes.bool.isRequired,
	jobs: PropTypes.array.isRequired,
	openForcedVehicleInvoicingModal: PropTypes.func.isRequired,
};

PaymentDetails.defaultProps = {
	isNewJob: false,
	clientContacts: [],
	positionWithPayCodeExist: false,
	clients: [],
};

export default PaymentDetails;
